import { useState, useEffect, useRef, useCallback } from 'react';
import { Wifi, WifiOff, X, ChevronDown, UploadCloud, AlertTriangle, RefreshCw, Trash2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  getPendingCounts,
  subscribePending,
  retryPendingNow,
  discardBlocked,
  type PendingCounts,
} from '../lib/inspectionSync';
import { ConfirmModal } from './ConfirmModal';

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL as string) ?? '';

interface ConnectionInfo {
  online: boolean;
  downlinkMbps: number | null;
  effectiveType: string | null;
  rtt: number | null;
  saveData: boolean;
  lastChecked: Date;
  apiReachable: boolean | null;
  apiLatencyMs: number | null;
}

interface NetworkInformation extends EventTarget {
  downlink?: number;
  effectiveType?: string;
  rtt?: number;
  saveData?: boolean;
  addEventListener(type: 'change', listener: () => void): void;
  removeEventListener(type: 'change', listener: () => void): void;
}

function getNetworkInfo(): Omit<ConnectionInfo, 'lastChecked' | 'apiReachable' | 'apiLatencyMs'> {
  const nav = navigator as Navigator & { connection?: NetworkInformation };
  const conn = nav.connection;
  return {
    online: navigator.onLine,
    downlinkMbps: conn?.downlink ?? null,
    effectiveType: conn?.effectiveType ?? null,
    rtt: conn?.rtt ?? null,
    saveData: conn?.saveData ?? false,
  };
}

interface ConnectionStatusProps {
  colors: {
    text_primary_color: string;
    text_secondary_color: string;
    card_bg_color: string;
    border_color: string;
    hover_bg_color: string;
  };
}

export function ConnectionStatus({ colors }: ConnectionStatusProps) {
  const { t } = useTranslation('common');
  const [info, setInfo] = useState<ConnectionInfo>({
    ...getNetworkInfo(),
    lastChecked: new Date(),
    apiReachable: null,
    apiLatencyMs: null,
  });
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [pending, setPending] = useState<PendingCounts>({ unsyncedDrafts: 0, queuedPhotos: 0, blockedDrafts: 0 });
  const [retrying, setRetrying] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Track inspection work saved on this device that hasn't reached the server
  // yet (drafts edited offline + queued photos). Refreshes on mount, whenever
  // the sync engine reports progress, and on every connectivity change so the
  // count drops to zero as soon as a reconnect drains the queue.
  const refreshPending = useCallback(() => {
    void getPendingCounts().then(setPending).catch(() => {});
  }, []);

  useEffect(() => {
    refreshPending();
    const unsub = subscribePending(refreshPending);
    window.addEventListener('online', refreshPending);
    window.addEventListener('offline', refreshPending);
    return () => {
      unsub();
      window.removeEventListener('online', refreshPending);
      window.removeEventListener('offline', refreshPending);
    };
  }, [refreshPending]);

  const pendingTotal = pending.unsyncedDrafts + pending.queuedPhotos;
  const hasBlocked = pending.blockedDrafts > 0;

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    try {
      await retryPendingNow();
    } finally {
      setRetrying(false);
      refreshPending();
    }
  }, [refreshPending]);

  const handleDiscard = useCallback(async () => {
    setDiscarding(true);
    try {
      await discardBlocked();
    } finally {
      setDiscarding(false);
      setShowDiscard(false);
      refreshPending();
    }
  }, [refreshPending]);

  const checkApi = useCallback(async () => {
    try {
      const start = performance.now();
      const res = await fetch(`${apiBaseUrl}/api/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });
      const latency = Math.round(performance.now() - start);
      setInfo(prev => ({
        ...prev,
        apiReachable: res.status < 500,
        apiLatencyMs: latency,
        lastChecked: new Date(),
      }));
    } catch {
      setInfo(prev => ({
        ...prev,
        apiReachable: false,
        apiLatencyMs: null,
        lastChecked: new Date(),
      }));
    }
  }, []);

  useEffect(() => {
    const updateNetwork = () => {
      setInfo(prev => ({
        ...prev,
        ...getNetworkInfo(),
        lastChecked: new Date(),
      }));
    };

    window.addEventListener('online', updateNetwork);
    window.addEventListener('offline', updateNetwork);

    const nav = navigator as Navigator & { connection?: NetworkInformation };
    const conn = nav.connection;
    if (conn) {
      conn.addEventListener('change', updateNetwork);
    }

    checkApi();
    const interval = setInterval(checkApi, 30000);

    return () => {
      window.removeEventListener('online', updateNetwork);
      window.removeEventListener('offline', updateNetwork);
      if (conn) {
        conn.removeEventListener('change', updateNetwork);
      }
      clearInterval(interval);
    };
  }, [checkApi]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        tooltipRef.current && !tooltipRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setIsTooltipOpen(false);
      }
    };
    if (isTooltipOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isTooltipOpen]);

  const isOnline = info.online;
  const isHealthy = isOnline && info.apiReachable !== false;

  const statusColor = !isOnline
    ? '#ef4444'
    : info.apiReachable === false
      ? '#f59e0b'
      : '#22c55e';

  const effectiveLabel = (type: string | null) => {
    if (!type) return t('connection.unknown');
    const map: Record<string, string> = {
      'slow-2g': '2G (slow)',
      '2g': '2G',
      '3g': '3G',
      '4g': '4G / LTE',
    };
    return map[type] || type;
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => {
          if (!isTooltipOpen) checkApi();
          setIsTooltipOpen(!isTooltipOpen);
        }}
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg transition-colors"
        style={{ color: colors.text_primary_color, backgroundColor: 'transparent' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hover_bg_color}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title={isHealthy ? t('connection.connected') : t('connection.disconnected')}
      >
        {isOnline ? (
          <Wifi size={16} style={{ color: statusColor }} />
        ) : (
          <WifiOff size={16} style={{ color: statusColor }} />
        )}
        <div
          className="w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: statusColor }}
        />
        {pendingTotal > 0 && (
          <span
            className="flex items-center gap-0.5 text-[10px] font-semibold leading-none px-1 py-0.5 rounded"
            style={
              hasBlocked
                ? { backgroundColor: '#ef444422', color: '#ef4444' }
                : { backgroundColor: '#f59e0b22', color: '#f59e0b' }
            }
            title={hasBlocked ? t('connection.uploadFailed') : t('connection.willUploadWhenOnline')}
          >
            {hasBlocked ? <AlertTriangle size={11} /> : <UploadCloud size={11} />}
            {pendingTotal}
          </span>
        )}
        <ChevronDown size={12} style={{ color: colors.text_secondary_color }} />
      </button>

      {isTooltipOpen && (
        <>
          <div className="fixed inset-0 z-50" onClick={() => setIsTooltipOpen(false)} />
          <div
            ref={tooltipRef}
            className="absolute right-0 mt-1 w-72 rounded-xl shadow-xl z-50 overflow-hidden"
            style={{ backgroundColor: colors.card_bg_color, border: `1px solid ${colors.border_color}` }}
          >
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ borderBottom: `1px solid ${colors.border_color}` }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: statusColor }}
                />
                <span className="text-sm font-semibold" style={{ color: colors.text_primary_color }}>
                  {isOnline ? t('connection.online') : t('connection.offline')}
                </span>
              </div>
              <button
                onClick={() => setIsTooltipOpen(false)}
                className="p-1 rounded-lg transition-colors"
                style={{ color: colors.text_secondary_color }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hover_bg_color}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <X size={14} />
              </button>
            </div>

            <div className="px-4 py-3 space-y-2.5">
              <InfoRow
                label={t('connection.browserStatus')}
                value={isOnline ? t('connection.online') : t('connection.offline')}
                valueColor={isOnline ? '#22c55e' : '#ef4444'}
                colors={colors}
              />
              <InfoRow
                label={t('connection.serverStatus')}
                value={
                  info.apiReachable === null
                    ? t('connection.checking')
                    : info.apiReachable
                      ? t('connection.reachable')
                      : t('connection.unreachable')
                }
                valueColor={
                  info.apiReachable === null
                    ? colors.text_secondary_color
                    : info.apiReachable
                      ? '#22c55e'
                      : '#ef4444'
                }
                colors={colors}
              />
              {info.apiLatencyMs !== null && (
                <InfoRow
                  label={t('connection.latency')}
                  value={`${info.apiLatencyMs} ms`}
                  valueColor={
                    info.apiLatencyMs < 200
                      ? '#22c55e'
                      : info.apiLatencyMs < 500
                        ? '#f59e0b'
                        : '#ef4444'
                  }
                  colors={colors}
                />
              )}
              {info.effectiveType && (
                <InfoRow
                  label={t('connection.networkType')}
                  value={effectiveLabel(info.effectiveType)}
                  colors={colors}
                />
              )}
              {info.downlinkMbps !== null && (
                <InfoRow
                  label={t('connection.bandwidth')}
                  value={`${info.downlinkMbps} Mbps`}
                  colors={colors}
                />
              )}
              {info.rtt !== null && (
                <InfoRow
                  label={t('connection.roundTrip')}
                  value={`${info.rtt} ms`}
                  colors={colors}
                />
              )}
              {info.saveData && (
                <InfoRow
                  label={t('connection.dataSaver')}
                  value={t('connection.enabled')}
                  valueColor="#f59e0b"
                  colors={colors}
                />
              )}
              {pendingTotal > 0 && (
                <InfoRow
                  label={t('connection.pendingUploads', { count: pendingTotal })}
                  value={t('connection.savedOnDevice')}
                  valueColor="#f59e0b"
                  colors={colors}
                />
              )}
            </div>

            {pendingTotal > 0 && !hasBlocked && (
              <div
                className="flex items-center gap-2 px-4 py-2.5 text-xs"
                style={{ borderTop: `1px solid ${colors.border_color}`, color: '#b45309', backgroundColor: '#fffbeb' }}
              >
                <UploadCloud size={14} className="flex-shrink-0" />
                <span>{t('connection.willUploadWhenOnline')}</span>
              </div>
            )}

            {hasBlocked && (
              <div
                className="px-4 py-2.5 text-xs"
                style={{ borderTop: `1px solid ${colors.border_color}`, color: '#991b1b', backgroundColor: '#fef2f2' }}
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">
                      {t('connection.blockedReports', { count: pending.blockedDrafts })}
                    </div>
                    <div className="mt-0.5" style={{ color: '#b91c1c' }}>
                      {t('connection.blockedHelp')}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {pendingTotal > 0 && (
              <div
                className="flex items-center gap-2 px-4 py-2.5"
                style={{ borderTop: `1px solid ${colors.border_color}` }}
              >
                <button
                  onClick={handleRetry}
                  disabled={retrying || discarding}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {retrying ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                  {retrying ? t('connection.retrying') : t('connection.retryNow')}
                </button>
                {hasBlocked && (
                  <button
                    onClick={() => setShowDiscard(true)}
                    disabled={retrying || discarding}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-700 dark:text-red-300 border border-red-300 dark:border-red-500/30 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={13} />
                    {t('connection.discard')}
                  </button>
                )}
              </div>
            )}

            <div
              className="px-4 py-2 text-xs"
              style={{ borderTop: `1px solid ${colors.border_color}`, color: colors.text_secondary_color }}
            >
              {t('connection.lastChecked')}: {info.lastChecked.toLocaleTimeString()}
            </div>
          </div>
        </>
      )}

      <ConfirmModal
        isOpen={showDiscard}
        variant="danger"
        title={t('connection.discardTitle')}
        message={t('connection.discardMessage', { count: pending.blockedDrafts })}
        confirmText={t('connection.discard')}
        onConfirm={handleDiscard}
        onCancel={() => setShowDiscard(false)}
        isLoading={discarding}
        loadingText={t('connection.discarding')}
      />
    </div>
  );
}

function InfoRow({
  label,
  value,
  valueColor,
  colors,
}: {
  label: string;
  value: string;
  valueColor?: string;
  colors: { text_primary_color: string; text_secondary_color: string };
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs" style={{ color: colors.text_secondary_color }}>{label}</span>
      <span
        className="text-xs font-medium"
        style={{ color: valueColor || colors.text_primary_color }}
      >
        {value}
      </span>
    </div>
  );
}
